/**
 * Demo data inject/cleanup for the Wallboard segment of the Welcome Tour.
 *
 * Injects:
 * - Demo machine with embedded `demoBatch` and `demoMachineEvents`
 *   into the machines list via `prependDemoMachine` (first tile position)
 * - Registry entries for timeline events (wallboard calls getMachineEventTimelineByBatchId)
 * - Registry entry for NC file content (ActiveTileNCFileSnapshot fetches via ncFileContentHash)
 * - setTourActive(true) to block getMachines/updateMachineInfo during tour
 *
 * Uses 999990xxx ID range (welcome tour range), NOT the local wallboard tour's 999999xxx.
 */

import { AppThunk } from '@store/types';
import { prependDemoMachine, removeDemoMachine } from '@reducers/machines';
import {
  registerDemoBatch,
  registerDemoTimelineEvents,
  registerDemoNcFileContent,
  clearDemoRegistry,
  setTourActive,
} from '../../../../helpers/demoDataRegistry';
import { DEMO_WELCOME_MACHINE_ID, DEMO_BATCH_ID } from '@components/tours/config/global/welcome/constants';
import { DEMO_OEE_VALUE } from '@components/tours/config/wallboard/constants';
import {
  createDemoMachine,
  createDemoBatchStatistics,
  createDemoTimelineEvents,
  createDemoNcFileContent,
} from './fixtures';

/**
 * Build a wallboard-specific demo machine with embedded demoBatch.
 *
 * The wallboard's `item.js` uses `useState(machine.demoBatch || null)` on mount,
 * so embedding the batch data skips the API call entirely.
 */
const createWallboardDemoMachine = () => {
  const baseMachine = createDemoMachine();
  const batchStats = createDemoBatchStatistics();
  const timelineEvents = createDemoTimelineEvents();

  return {
    ...baseMachine,
    demoOee: DEMO_OEE_VALUE,
    demoBatch: {
      ...batchStats,
      // Wallboard needs Linked status + quantity target for pieces progress bar
      linkStatus: 'Linked',
      quantityTarget: 25,
      links: [
        {
          productionOperationId: 999990105,
          quantityTarget: 25,
          productionBatchId: DEMO_BATCH_ID,
        },
      ],
      // Embedded timeline events for colored batch timeline segments
      demoMachineEvents: timelineEvents,
    },
  };
};

/**
 * Inject demo data for the Wallboard segment.
 */
export const injectWallboardWelcomeDemo = (): AppThunk => (dispatch) => {
  // Block real API calls (getMachines, updateMachineInfo) during tour
  setTourActive(true);

  // Register in registry — wallboard calls getMachineEventTimelineByBatchId for timeline rendering
  const batchStats = createDemoBatchStatistics();
  const timelineEvents = createDemoTimelineEvents();
  registerDemoBatch(DEMO_BATCH_ID, batchStats);
  registerDemoTimelineEvents(DEMO_BATCH_ID, timelineEvents);

  // Register NC file content — ActiveTileNCFileSnapshot fetches via ncFileContentHash
  registerDemoNcFileContent('demo-nc-file-hash-001', createDemoNcFileContent());

  // Prepend demo machine as first tile
  const wallboardDemoMachine = createWallboardDemoMachine();
  dispatch(prependDemoMachine(wallboardDemoMachine));
};

/**
 * Cleanup demo data after Wallboard segment ends.
 */
export const cleanupWallboardWelcomeDemo = (): AppThunk => (dispatch) => {
  // Remove demo machine from machines list
  dispatch(removeDemoMachine(DEMO_WELCOME_MACHINE_ID));

  // Clear registry + tour active flag
  clearDemoRegistry();
  setTourActive(false);
};
